import React, { Dispatch, SetStateAction } from 'react';
import {
  Control,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';

import { Button, Modal, Visible } from 'coreOld/components';
import { GoBack, WrapperBlock } from 'coreOld/elements';
import { ButtonType, Size } from 'coreOld/enums';
import { isEmptyArray, isObject } from 'coreOld/utils';

import FieldChildView from './FieldChildView';

interface IProps<T> {
  isOpen: boolean;
  onClose: (reset) => void;
  register: UseFormRegister<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  reset?: UseFormReset<T>;
  setError?: Dispatch<SetStateAction<string>>;
  title: string;
  submitBtnLabel?: string;
  removeBtnLabel?: string;
  subtitle?: string;
  selectedItem?: any;
  allowRemove?: boolean;
  onRemove?: (reset?: UseFormReset<T>) => void;
  control?: Control<T, object>;
  setValue?: UseFormSetValue<T>;
  getValues?: UseFormGetValues<T>;
  errors?;
  onSubmit: (
    reset: UseFormReset<T>,
    setError: Dispatch<SetStateAction<string>>
  ) => (data: T) => void;
  disabledSubmit?: boolean;
  hasGoBack?: boolean;
  goBack?: () => void;
  size?: Size;
  minWidth?: number;
  schema;
  children?;
  beforeSubmitContent?: JSX.Element;
  footerContent?: JSX.Element;
}

const FormView = <T,>({
  isOpen,
  onClose,
  children,
  beforeSubmitContent,
  footerContent,
  selectedItem,
  title,
  submitBtnLabel,
  removeBtnLabel,
  allowRemove = false,
  hasGoBack = false,
  subtitle,
  handleSubmit,
  reset,
  onRemove,
  control,
  setValue,
  getValues,
  errors,
  size,
  minWidth,
  setError,
  onSubmit,
  disabledSubmit = false,
  goBack,
}: IProps<T>) => {
  const inputsChildren = Boolean(
    children?.length > 0 ? children?.[0]?.props?.name : children?.props?.name
  );

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      isOpen={isOpen}
      onClose={() => onClose(reset)}
      size={size || Size.SM}
      minWidth={minWidth}
    >
      <Visible when={Boolean(children) && !isEmptyArray(children)}>
        <WrapperBlock size={size} inputsWrapper={inputsChildren}>
          {React.Children.map(children, (child) => {
            return child?.props?.name ? (
              <FieldChildView
                child={child}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                control={control}
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                reset={reset}
                setError={setError}
              />
            ) : (
              child
            );
          })}
        </WrapperBlock>

        <Visible when={Boolean(beforeSubmitContent)}>
          <WrapperBlock>{beforeSubmitContent}</WrapperBlock>
        </Visible>

        <WrapperBlock hideBorderBottom={!footerContent && !goBack} buttonWrapper>
          <Button
            onClick={handleSubmit(onSubmit(reset, setError))}
            disabled={disabledSubmit}
            style={{ marginBottom: 8 }}
          >
            {submitBtnLabel}
          </Button>

          <Visible when={allowRemove && isObject(selectedItem)}>
            <Button onClick={() => onRemove(reset)} btnType={ButtonType.DANGER}>
              {removeBtnLabel}
            </Button>
          </Visible>
        </WrapperBlock>
      </Visible>

      <Visible when={hasGoBack && Boolean(goBack)}>
        <WrapperBlock hideBorderBottom={!footerContent}>
          <GoBack onClick={goBack} />
        </WrapperBlock>
      </Visible>

      <Visible when={Boolean(footerContent)}>
        <WrapperBlock hideBorderBottom>{footerContent}</WrapperBlock>
      </Visible>
    </Modal>
  );
};

export default FormView;
